import { BulkRequest, CurrencyType, Id } from "@dgs/core";
import { FilledGuestAttribute, GuestOptionResource, GuestStatus } from "./guest";
import { GuestTypeStepperResource } from "./guestType";
import { HotelOptionResource } from "./hotel";
import { HotelStayTypeOptionByGuestResource, HotelStayTypeOptionsResource } from "./hotelStayType";
import { RoomCategoryOptionResource } from "./roomCategory";
import { RoomTypeByHotelStayResource, RoomTypeOptionResource } from "./roomType";
import { CostCoverage } from "./stayConfiguration";

export enum HotelStayStatus {
	INQUIRY = "INQUIRY",
	BOOKING = "BOOKING",
	CONFLICT = "CONFLICT",
	CANCELLATION_REQUESTED = "CANCELLATION_REQUESTED",
}

interface BaseGuestHotelStayResource {
	id: Id;
	hotelStayType: HotelStayTypeOptionByGuestResource;
	status: HotelStayStatus;
	checkIn: string;
	checkOut: string;
	requestedHotel: HotelOptionResource | null;
	requestedRoomType: RoomTypeOptionResource | null;
	remarkByGuest: string | null;
	remarkForAdmin: string | null;
	remarkForGuest: string | null;
	additionalPersonsText: string | null;
	subStays: HotelSubStayIndexResource[];
	booking: GuestHotelStayBookingResource | null;
}

export interface GuestMainHotelStayResource extends BaseGuestHotelStayResource {
	mainStay: null;
	additionalPersonsEnabled: boolean;
}

export interface GuestSubHotelStayResource extends BaseGuestHotelStayResource {
	mainStay: HotelMainStayResource;
}

export type GuestHotelStayResource = GuestMainHotelStayResource | GuestSubHotelStayResource;

export interface HotelSubStayIndexResource {
	id: Id;
	status: HotelStayStatus;
	guest: GuestOptionResource;
	checkIn: string;
	checkOut: string;
	hasCheckInConflicts: boolean;
	hasCheckOutConflicts: boolean;
	remarkByGuest: string | null;
}

export interface HotelStayIndexResource {
	id: Id;
	requestedHotel: string | null;
	requestedRoomType: string | null;
	bookedHotel: string | null;
	bookedRoomType: string | null;
	processedBy: string | null;
	attributes: FilledGuestAttribute[];
	checkIn: string | null;
	checkOut: string | null;
	remarkByGuest: string | null;
	additionalPersonsText: string | null;
	firstName: string;
	lastName: string;
	email: string;
}

export interface HotelStayResource {
	id: Id;
	guest: HotelStayGuestResource;
	remarkByGuest: string;
	status: HotelStayStatus;
	checkIn: string;
	checkOut: string;
	requestedRoomType: RoomTypeOptionResource | null;
	requestedHotel: HotelOptionResource | null;
}

export interface HotelStayOptionResource {
	id: Id;
	hotelStayType: HotelStayTypeOptionsResource;
	status: string;
	checkIn: string;
	checkOut: string;
}

export enum HotelStayMatchStatus {
	STAY_AVAILABLE = "STAY_AVAILABLE",
	IS_SUB_STAY = "IS_SUB_STAY",
	MISMATCHING_HOTEL_STAY_TYPE = "MISMATCHING_HOTEL_STAY_TYPE",
}

export interface HotelStayOptionWithMatchResource extends HotelStayOptionResource {
	matchStatus: HotelStayMatchStatus;
}

interface HotelStayResolveConflictRequestAddDates {
	hotelStayId: Id;
	addDates: string[];
}

interface HotelStayResolveConflictRequestChangeSubStayDates {
	hotelStayId: Id;
	changeSubStayPeriod: ChangeSubStayPeriodFormValues;
}

interface HotelStayResolveConflictRequestRemoveDates {
	hotelStayId: Id;
	deleteDates: string[];
}

interface HotelStayResolveConflictRequestChangeRoomType {
	hotelStayId: Id;
	changeRoomTypeId: Id;
}

export type HotelStayResolveConflictRequest =
	| HotelStayResolveConflictRequestAddDates
	| HotelStayResolveConflictRequestRemoveDates
	| HotelStayResolveConflictRequestChangeRoomType
	| HotelStayResolveConflictRequestChangeSubStayDates;

export interface HotelSubStayFormValues {
	status: HotelStayStatus;
	hotelStayType: HotelStayTypeOptionsResource;
	guest: GuestOptionResource;
	checkIn: string;
	checkOut: string;
	remarkByGuest: string | null;
	remarkForAdmin: string | null;
	existingStays: HotelStayOptionResource[];
}

export interface HotelSubStayUpdateRequest {
	hotelStayTypeId: Id;
	checkIn: string;
	checkOut: string;
	remarkByGuest: string | null;
	remarkForAdmin: string | null;
}

export interface HotelSubStayUpdateResource extends HotelSubStayFormValues {
	id: Id;
}

export interface HotelMainStayResource {
	id: Id;
	status: HotelStayStatus;
	guest: GuestOptionResource;
	checkIn: string;
	checkOut: string;
	requestedHotel: HotelOptionResource | null;
	requestedRoomType: RoomTypeOptionResource | null;
	remarkByGuest: string | null;
	additionalPersonsText: string | null;
	additionalPersonsEnabled: boolean;
}

export interface GuestHotelStayFormValues {
	status: HotelStayStatus;
	hotelStayType: HotelStayTypeOptionByGuestResource | null;
	checkIn: string;
	checkOut: string;
	requestedRoomType: RoomTypeOptionResource | null;
	remarkByGuest: string | null;
	remarkForAdmin: string | null;
	additionalPersonsText: string | null;
	additionalPersonsEnabled: boolean;
}

export interface HotelStayInquiryBulkCreateFormValues {
	hotelStayType: HotelStayTypeOptionByGuestResource | null;
	checkIn: string;
	checkOut: string;
	requestedRoomType: RoomTypeOptionResource | null;
	remarkByGuest: string | null;
	remarkForAdmin: string | null;
}

export interface HotelStayInquiryBulkCreateRequest extends HotelStayBulkProcessRequest {
	guestTypeId: Id;
	hotelStayTypeId: Id;
	checkIn: string;
	checkOut: string;
	requestedHotelId: Id | null;
	requestedRoomTypeId: Id | null;
	remarkByGuest: string | null;
	remarkForAdmin: string | null;
}

export type GuestHotelStayCreateResource = GuestHotelStayFormValues;

export interface HotelStayUpdateRequest {
	hotelStayTypeId: Id;
	checkIn: string;
	checkOut: string;
	requestedHotelId: Id | null;
	requestedRoomTypeId: Id | null;
	remarkByGuest: string | null;
	remarkForAdmin: string | null;
	additionalPersonsText: string | null;
	additionalPersonsEnabled: boolean;
	subStays: HotelSubStayRequest[];
}

export interface HotelStayCreateRequest {
	guestId: Id;
	hotelStayTypeId: Id;
	checkIn: string;
	checkOut: string;
	requestedHotelId: Id | null;
	requestedRoomTypeId: Id | null;
	remarkByGuest: string | null;
	remarkForAdmin: string | null;
	additionalPersonsText: string | null;
	additionalPersonsEnabled: boolean;
}

export interface HotelSubStayCreateRequest {
	mainStayId: Id;
	guestId: Id;
	checkIn: string;
	checkOut: string;
	remarkByGuest: string | null;
	shouldBook: boolean;
	remarkForAdmin: string | null;
}

export interface HotelStayProcessResource {
	id: Id;
	checkIn: string;
	checkOut: string;
	requestedRoomType: RoomTypeByHotelStayResource | null;
	guest: GuestOptionResource;
	subStays: HotelSubStayProcessResource[];
	additionalPersonsText: string | null;
	remarkForAdmin: string | null;
	remarkForGuest: string | null;
	remarkByGuest: string | null;
}

export interface HotelStayProcessedResource {
	id: Id;
	status: HotelStayStatus;
}

export interface HotelStayBulkProcessedResource {
	successes: number;
	failures: number;
}

export interface HotelSubStayBookingResource {
	id: Id;
	guest: GuestOptionResource;
	status: HotelStayStatus;
	checkIn: string;
	checkOut: string;
	remarkByGuest: string | null;
}

export interface HotelStayBookingUpdateResource {
	id: Id;
	guest: GuestOptionResource;
	roomType: RoomTypeByHotelStayResource;
	remarkForAdmin: string | null;
	remarkForGuest: string | null;
	status: HotelStayStatus;
	checkIn: string;
	checkOut: string;
	subStays: HotelSubStayBookingResource[];
}

export interface HotelSubStayProcessResource {
	id: Id;
	checkIn: string;
	checkOut: string;
	guest: GuestOptionResource;
	status: HotelStayStatus;
}

interface HotelSubStayRequest {
	id: Id;
	checkIn: string;
	checkOut: string;
}

export interface HotelStayBookingUpdateRequest {
	checkIn: string;
	checkOut: string;
	roomTypeId: number;
	subStays: HotelSubStayRequest[];
	remarkForAdmin: string | null;
	remarkForGuest: string | null;
}

export interface HotelStayBulkProcessRequest extends BulkRequest {
	filterId: Id | null;
}

export interface GuestHotelStayRoomBookingResource {
	id: Id;
	date: string;
	price: number;
	currency: CurrencyType;
	costCoverage: CostCoverage;
	roommates: GuestOptionResource[];
}
export interface GuestHotelStayBookingResource {
	checkIn: string;
	checkOut: string;
	hotel: HotelOptionResource;
	roomType: RoomTypeOptionResource;
	remarkForGuest: string;
	remarkForAdmin: string;
	roomBookings: GuestHotelStayRoomBookingResource[];
}
export interface HotelStayBookingsRequest {
	filterId: Id | null;
	guestViewId: Id;
}

export interface HotelStayExportRequest {
	filterId: Id | null;
	guestViewId: Id;
	locale: string;
}

export interface HotelStayBookingResource {
	id: Id;
	hotelName: string;
	roomTypeName: string;
	firstName: string;
	lastName: string;
	numberOfNights: number;
	numberOfPersons: number;
	roommates: GuestOptionResource[];
	bookedCheckIn: string;
	bookedCheckOut: string;
	remarkByGuest: string | null;
	remarkForAdmin: string | null;
	remarkForGuest: string | null;
	additionalPersonsText: string | null;
	attributes: FilledGuestAttribute[];
	subStays: HotelStayBookingResource[];
	roomCategory: RoomCategoryOptionResource;
	costCoverage: CostCoverage;
	guestId: string;
}

export interface BookedNightsIndexResource {
	count: number;
}

export interface GuestHotelBookingIndexResource {
	id: Id;
	stayType: { id: Id; name: string } | null;
	isSubStay: boolean;
	checkIn: string | null;
	checkOut: string | null;
	roomTypeName: string | null;
	hotelName: string | null;
}

export enum HotelStayConflictCause {
	UNREGISTERED_GUEST = "UNREGISTERED_GUEST",
	CANCELLATION_REQUESTED = "CANCELLATION_REQUESTED",
	REQUESTED_STAY_SHORTENING = "REQUESTED_STAY_SHORTENING",
	REQUESTED_SUB_STAY_OUTSIDE_MAIN_STAY_PERIOD = "REQUESTED_SUB_STAY_OUTSIDE_MAIN_STAY_PERIOD",
	EXTENSION_OF_STAY_FULL_AVAILABILITY_ALTERNATIVE_ROOM_TYPE = "EXTENSION_OF_STAY_FULL_AVAILABILITY_ALTERNATIVE_ROOM_TYPE",
	EXTENSION_OF_STAY_FULL_AVAILABILITY_NO_ALTERNATIVE_ROOM_TYPE = "EXTENSION_OF_STAY_FULL_AVAILABILITY_NO_ALTERNATIVE_ROOM_TYPE",
	EXTENSION_OF_STAY_PARTIAL_AVAILABILITY_ALTERNATIVE_ROOM_TYPE = "EXTENSION_OF_STAY_PARTIAL_AVAILABILITY_ALTERNATIVE_ROOM_TYPE",
	EXTENSION_OF_STAY_PARTIAL_AVAILABILITY_NO_ALTERNATIVE_ROOM_TYPE = "EXTENSION_OF_STAY_PARTIAL_AVAILABILITY_NO_ALTERNATIVE_ROOM_TYPE",
	EXTENSION_OF_STAY_PARTIAL_AVAILABILITY_NO_CONTINUOUS_PERIOD_ALTERNATIVE_ROOM_TYPE = "EXTENSION_OF_STAY_PARTIAL_AVAILABILITY_NO_CONTINUOUS_PERIOD_ALTERNATIVE_ROOM_TYPE",
	EXTENSION_OF_STAY_PARTIAL_AVAILABILITY_NO_CONTINUOUS_PERIOD_NO_ALTERNATIVE_ROOM_TYPE = "EXTENSION_OF_STAY_PARTIAL_AVAILABILITY_NO_CONTINUOUS_PERIOD_NO_ALTERNATIVE_ROOM_TYPE",
	EXTENSION_OF_STAY_NO_AVAILABILITY_NO_ALTERNATIVE_ROOM_TYPE = "EXTENSION_OF_STAY_NO_AVAILABILITY_NO_ALTERNATIVE_ROOM_TYPE",
	EXTENSION_OF_STAY_NO_AVAILABILITY_ALTERNATIVE_ROOM_TYPE = "EXTENSION_OF_STAY_NO_AVAILABILITY_ALTERNATIVE_ROOM_TYPE",
}

interface HotelStayGuestResource extends GuestOptionResource {
	status: GuestStatus;
}

export interface HotelStayConflictCauseDateResource {
	date: string;
	isAvailable: boolean;
}

export interface HotelStayConflictCauseResource {
	conflictCause: HotelStayConflictCause;
	dates: HotelStayConflictCauseDateResource[];
}

export interface ChangeSubStayPeriodFormValues {
	checkIn: string;
	checkOut: string;
}

export interface HotelSubStayConflictResource {
	id: Id;
	guest: HotelStayGuestResource;
	checkIn: string;
	checkOut: string;
	bookedCheckIn: string | null;
	bookedCheckOut: string | null;
	status: HotelStayStatus;
	conflictCauses: HotelStayConflictCauseResource[];
}

export interface HotelStayConflictResource {
	id: Id;
	guest: HotelStayGuestResource;
	remarkByGuest: string | null;
	remarkForAdmin: string | null;
	remarkForGuest: string | null;
	checkIn: string;
	checkOut: string;
	bookedCheckIn: string;
	bookedCheckOut: string;
	roomType: RoomTypeOptionResource;
	conflictCauses: HotelStayConflictCauseResource[];
	subStays: HotelSubStayConflictResource[];
}

export interface MainHotelStayFormValues {
	checkIn: string;
	checkOut: string;
	roomType: RoomTypeByHotelStayResource | null;
	remarkForAdmin: string | null;
	remarkForGuest: string | null;
}

export interface SubHotelStayFormValues {
	id: Id;
	shouldBook: "yes" | "no";
	checkIn: string;
	checkOut: string;
}

export interface SubHotelStaysFormValues {
	subStays: SubHotelStayFormValues[];
}

export interface HotelStayBookingFormValues extends HotelStayBookingUpdateResource {
	subStays: (HotelSubStayBookingResource & {
		shouldBook: "yes" | "no";
	})[];
}

export interface HotelStayStep extends GuestTypeStepperResource {
	index: number;
}
